import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import * as React from 'react';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { classNames } from "../../utils/helpers";
import { Actionable } from "../actionable";
import { Text } from "../text";
import { Icon } from "../icon";
import { View } from "../view";
import { Info } from '@az/starc-ui-icons';
import s from './SearchBar.module.css';
export const Suggestions = ({ suggestionsClassName, inputValue, suggestions, searchInputRef, searchRef, setSuggestionValue, setSuggestionWasHidden, setIsSuggestion, onSelectionChange, resultsHeaderText, narrowSearchText, onTabEvent, onNarrowSearchClick, onHideSuggestions, maxMenuHeight, inputId, offset, }) => {
    const ref = React.useRef(null);
    const suggestionContainerRef = React.useRef(null);
    const [highlightedSuggestion, setHighlightedSuggestion] = React.useState(-1);
    const onNarrowSearch = React.useRef(false);
    const narrowSearchItemRef = React.useRef(null);
    const handleSuggestionClick = (e, suggestion) => {
        e.preventDefault();
        const input = document.getElementById(inputId);
        input && input.focus();
        onSelectionChange(suggestion, true);
    };
    React.useEffect(() => {
        const inputBox = searchInputRef === null || searchInputRef === void 0 ? void 0 : searchInputRef.current;
        const clearSearchButton = searchRef === null || searchRef === void 0 ? void 0 : searchRef.current;
        const suggestionBox = suggestionContainerRef === null || suggestionContainerRef === void 0 ? void 0 : suggestionContainerRef.current;
        const handleClickOutside = (event) => {
            if (event.target !== inputBox && event.target !== suggestionBox) {
                setIsSuggestion(false);
                onTabEvent(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        const handleKeyDown = (event) => {
            var _a, _b, _c, _d, _e, _f;
            if (event.key !== 'Tab' &&
                event.key !== 'ArrowDown' &&
                event.key !== 'Backspace' &&
                event.key !== 'ArrowUp' &&
                event.key !== 'Enter') {
                setIsSuggestion(false);
            }
            if (event.key === 'Tab') {
                event.preventDefault();
            }
            const setFocus = (direction) => {
                const currentSuggestion = document.activeElement;
                const nextSuggestion = direction === 'next'
                    ? currentSuggestion === null || currentSuggestion === void 0 ? void 0 : currentSuggestion.nextElementSibling
                    : currentSuggestion === null || currentSuggestion === void 0 ? void 0 : currentSuggestion.previousElementSibling;
                nextSuggestion && nextSuggestion.focus();
                return;
            };
            switch (event.key) {
                case 'Tab':
                    setIsSuggestion(false);
                    if (event.shiftKey) {
                        setIsSuggestion(false);
                        if (document.activeElement === inputBox && highlightedSuggestion === -1) {
                            const focusableElements = document.querySelectorAll('input:not([disabled]), button:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])');
                            const currentIndex = Array.from(focusableElements).indexOf(event.target);
                            const lastElement = focusableElements[currentIndex - 1];
                            if (lastElement) {
                                lastElement.focus();
                            }
                            onTabEvent(false);
                            break;
                        }
                        if (document.activeElement === narrowSearchItemRef.current ||
                            document.activeElement === clearSearchButton) {
                            if (clearSearchButton && document.activeElement !== clearSearchButton) {
                                clearSearchButton === null || clearSearchButton === void 0 ? void 0 : clearSearchButton.focus();
                            }
                            else {
                                onNarrowSearch.current = false;
                                inputBox === null || inputBox === void 0 ? void 0 : inputBox.focus();
                            }
                            break;
                        }
                        onNarrowSearch.current = true;
                        (_a = narrowSearchItemRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                        break;
                    }
                    if (document.activeElement === narrowSearchItemRef.current && onNarrowSearch.current) {
                        inputBox === null || inputBox === void 0 ? void 0 : inputBox.focus();
                        break;
                    }
                    if (clearSearchButton && document.activeElement !== clearSearchButton) {
                        clearSearchButton === null || clearSearchButton === void 0 ? void 0 : clearSearchButton.focus();
                    }
                    else {
                        onNarrowSearch.current = true;
                        (_b = narrowSearchItemRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                    }
                    break;
                case 'Backspace':
                    setIsSuggestion(false);
                    break;
                case 'ArrowDown':
                    setIsSuggestion(true);
                    setFocus('next');
                    if (suggestions[highlightedSuggestion] === suggestions[suggestions.length - 1]) {
                        setHighlightedSuggestion(0);
                        setSuggestionValue(suggestions[0], false);
                        (_c = suggestionContainerRef.current) === null || _c === void 0 ? void 0 : _c.children[0].scrollIntoView();
                    }
                    else {
                        setHighlightedSuggestion(highlightedSuggestion + 1);
                        setSuggestionValue(suggestions[highlightedSuggestion + 1], false);
                        (_d = suggestionContainerRef.current) === null || _d === void 0 ? void 0 : _d.children[highlightedSuggestion + 1].scrollIntoView();
                    }
                    break;
                case 'ArrowUp':
                    setIsSuggestion(true);
                    setFocus('previous');
                    if (highlightedSuggestion === 0) {
                        setHighlightedSuggestion(suggestions.length - 1);
                        setSuggestionValue(suggestions[suggestions.length - 1], false);
                        (_e = suggestionContainerRef.current) === null || _e === void 0 ? void 0 : _e.children[suggestions.length - 1].scrollIntoView();
                    }
                    else {
                        setHighlightedSuggestion(highlightedSuggestion - 1);
                        setSuggestionValue(suggestions[highlightedSuggestion - 1], false);
                        (_f = suggestionContainerRef.current) === null || _f === void 0 ? void 0 : _f.children[highlightedSuggestion - 1].scrollIntoView();
                    }
                    break;
                case 'Enter':
                    if (document.activeElement === narrowSearchItemRef.current) {
                        break;
                    }
                    setIsSuggestion(false);
                    onSelectionChange(suggestions[highlightedSuggestion], true);
                    break;
                case 'Escape':
                    setIsSuggestion(false);
                    setSuggestionWasHidden === null || setSuggestionWasHidden === void 0 ? void 0 : setSuggestionWasHidden(true);
                    onHideSuggestions();
                    break;
                default:
                    break;
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [
        highlightedSuggestion,
        suggestions,
        inputId,
        onTabEvent,
        setIsSuggestion,
        setSuggestionValue,
        setSuggestionWasHidden,
        searchRef,
        searchInputRef,
        onSelectionChange,
        onHideSuggestions,
    ]);
    const onMouseHover = (suggestion) => {
        const currentSuggestionIndex = suggestions.findIndex(currentSuggestion => suggestion === currentSuggestion);
        setHighlightedSuggestion(currentSuggestionIndex);
    };
    const renderSuggestion = (suggestion) => {
        // check if the suggestion is highlighted
        const isSelected = highlightedSuggestion !== -1 && suggestion === suggestions[highlightedSuggestion];
        return (_jsx(View.Item, { columns: 12, className: classNames(s.suggestion, isSelected && s['--active']), attributes: {
                onMouseDown: (e) => handleSuggestionClick(e, suggestion),
                onMouseEnter: () => onMouseHover(suggestion),
                role: 'option',
                tabIndex: 0,
                'aria-selected': isSelected,
            }, children: renderSuggestionText(suggestion) }, suggestion));
    };
    const renderSuggestionText = (suggestionText) => {
        const matches = match(suggestionText, inputValue, {
            insideWords: true,
            findAllOccurrences: false,
        });
        const parts = parse(suggestionText, matches);
        return (_jsx(View, { direction: "row", children: parts.map((part, index) => {
                return (_jsx(Text, { as: "span", attributes: { 'aria-label': part.text, 'data-testid': part.text }, weight: part.highlight ? 'regular' : 'bold', color: "primary", children: part.text }, index));
            }) }));
    };
    const className = classNames(s.suggestions, suggestionsClassName);
    return (_jsxs(View, { className: className, attributes: {
            style: {
                marginTop: offset ? `${offset}px` : undefined,
            },
            ref,
        }, children: [_jsxs(View, { direction: "row", padding: 4.5, children: [_jsx(View.Item, { grow: true, children: _jsx(Text, { as: "h1", color: "gray-500", size: "087", children: resultsHeaderText }) }), _jsx(Actionable, { onClick: onNarrowSearchClick, children: _jsxs(View, { direction: "row", align: "center", children: [_jsx(Text, { attributes: { tabIndex: 0, id: 'narrow-search-text', ref: narrowSearchItemRef }, size: "087", decoration: "underline", "aria-label": "Narrow search button", children: narrowSearchText }), _jsx(Icon, { svg: Info, size: 5 })] }) })] }), _jsx(View, { attributes: { id: 'suggestion-container', ref: suggestionContainerRef }, className: s['max-height'], maxHeight: `${maxMenuHeight}px`, children: suggestions.map(suggestion => {
                    return renderSuggestion(suggestion);
                }) })] }));
};
